import React, { useEffect, useMemo, useState } from 'react';
import { purchaseInvoicesService } from '../services/purchaseInvoicesService';
import { suppliersService } from '../services/suppliersService';
import { computePurchaseInvoiceTotals } from '../utils/purchaseInvoiceCalc';
import OcrUploadModal from '../components/OcrUploadModal';
import InvoiceImportConfirm from '../components/InvoiceImportConfirm';
import { Button, Badge, Input, Select, Textarea, Label, Icon } from '../components/ui';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../api/api';
import {
  IconFileInvoice, IconSync, IconDownload, IconFileSpreadsheet, IconMagic,
  IconPlus, IconPaperclip, IconEye, IconUnlink, IconEdit, IconTrash,
  IconSpinner, IconX, IconChevronLeft, IconChevronRight, IconFileExport,
} from '@tabler/icons-react';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import ExportModal from '../components/ExportModal';

const emptyInvoice = {
  supplierId: '',
  supplierInvoiceNumber: '',
  invoiceDate: new Date().toISOString().split('T')[0],
  dueDate: '',
  currency: 'TND',
  status: 'VALIDATED',
  lines: [{ description: '', qty: 1, unitPriceHT: 0, discountType: 'PERCENT', discountValue: 0, tvaRate: 19, stockItemId: null }],
  discountType: 'PERCENT',
  discountValue: 0,
  stampDuty: 1,
  notes: '',
};

export default function PurchaseInvoices() {
  const { workspaceStockEnabled } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [search, setSearch] = useState('');
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  const [showExportModal, setShowExportModal] = useState(false);

  const purchaseInvoiceColumns = [
    { key: 'supplier', label: 'Fournisseur', accessor: (inv) => { const s = typeof inv.supplierId === 'object' ? inv.supplierId : null; return s?.name || supplierById.get(inv?.supplierId)?.name || '—'; } },
    { key: 'supplierInvoiceNumber', label: 'N° facture', accessor: (inv) => inv?.supplierInvoiceNumber || '—' },
    { key: 'invoiceDate', label: 'Date', accessor: (inv) => inv?.invoiceDate || '—' },
    { key: 'status', label: 'Statut', accessor: (inv) => inv?.status || '—' },
    { key: 'total', label: 'Total TTC', accessor: (inv) => `${((inv?.grandTotal ?? inv?.totalTTC ?? 0)).toFixed(2)} DT` },
  ];

  useEffect(() => { setPage(1); }, [invoices.length]);

  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [form, setForm] = useState(emptyInvoice);
  const [uploadingId, setUploadingId] = useState(null);
  const [showOcrModal, setShowOcrModal] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  // ── OCR state ──
  const [ocrWarnings, setOcrWarnings] = useState(null); // warnings workspace → modale confirmation
  const [ocrFile, setOcrFile] = useState(null);         // fichier original pour attachement auto

  const normalizeListResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  };

  const loadSuppliers = async () => {
    try { setSuppliers(normalizeListResponse(await suppliersService.list())); }
    catch { setSuppliers([]); }
  };

  const loadInvoices = async () => {
    setLoading(true); setError('');
    try { setInvoices(normalizeListResponse(await purchaseInvoicesService.list())); }
    catch (e) {
      setError(e?.response?.data?.message || e?.message || "Backend non disponible.");
      setInvoices([]);
    } finally { setLoading(false); }
  };

  const loadStockItems = async () => {
    try {
      const res = await api.get('/stock/items', { params: { limit: 200 } });
      if (res.data.ok) setStockItems(res.data.items);
    } catch { setStockItems([]); }
  };

  useEffect(() => { loadSuppliers(); loadInvoices(); }, []);
  useEffect(() => { if (workspaceStockEnabled) loadStockItems(); }, [workspaceStockEnabled]);

  const supplierById = useMemo(() => {
    const map = new Map();
    suppliers.forEach((s) => map.set(s._id || s.id, s));
    return map;
  }, [suppliers]);

  const filteredInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) => {
      const supplier = supplierById.get(inv?.supplierId);
      return (supplier?.name || '').toLowerCase().includes(q)
        || (inv?.supplierInvoiceNumber || '').toLowerCase().includes(q)
        || (inv?.status || '').toLowerCase().includes(q);
    });
  }, [invoices, search, supplierById]);

  const totalPages = Math.ceil(filteredInvoices.length / PAGE_SIZE) || 1;
  const paginatedInvoices = filteredInvoices.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const closeModal = () => {
    setShowModal(false); setEditingInvoice(null);
    setForm(emptyInvoice); setError(''); setOcrFile(null);
  };

  const openCreate = () => {
    setEditingInvoice(null); setForm(emptyInvoice); setError(''); setShowModal(true);
  };

  const normalizeLine = (l) => ({
    description: l?.description || '', qty: l?.qty ?? 1, unitPriceHT: l?.unitPriceHT ?? 0,
    discountType: l?.discountType || 'PERCENT', discountValue: l?.discountValue ?? 0, tvaRate: l?.tvaRate ?? 19,
    stockItemId: l?.stockItemId || null,
  });

  const openEdit = (inv) => {
    setEditingInvoice(inv);
    setForm({
      supplierId: inv?.supplierId || '',
      supplierInvoiceNumber: inv?.supplierInvoiceNumber || '',
      invoiceDate: inv?.invoiceDate || new Date().toISOString().split('T')[0],
      dueDate: inv?.dueDate || '',
      currency: inv?.currency || 'TND',
      status: inv?.status || 'VALIDATED',
      lines: Array.isArray(inv?.lines) && inv.lines.length ? inv.lines.map(normalizeLine) : [normalizeLine({})],
      discountType: inv?.discountType || 'PERCENT',
      discountValue: inv?.discountValue ?? 0,
      stampDuty: inv?.stampDuty ?? 1,
      notes: inv?.notes || '',
    });
    setError(''); setShowModal(true);
  };

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const updateLine = (index, patch) => setForm((p) => {
    const lines = [...(p.lines || [])];
    lines[index] = { ...lines[index], ...patch };
    return { ...p, lines };
  });

  const addLine = () => setForm((p) => ({
    ...p,
    lines: [...(p.lines || []), { description: '', qty: 1, unitPriceHT: 0, discountType: 'PERCENT', discountValue: 0, tvaRate: 19, stockItemId: null }],
  }));

  const removeLine = (index) => setForm((p) => {
    const lines = [...(p.lines || [])];
    lines.splice(index, 1);
    return { ...p, lines: lines.length ? lines : [{ description: '', qty: 1, unitPriceHT: 0 }] };
  });

  const totals = useMemo(() => computePurchaseInvoiceTotals(form), [form]);

  const validate = () => {
    if (!form.supplierId) return 'Veuillez sélectionner un fournisseur.';
    if (!form.supplierInvoiceNumber.trim()) return 'Le numéro de facture fournisseur est obligatoire.';
    if (!form.invoiceDate) return 'La date de facture est obligatoire.';
    if (!Array.isArray(form.lines) || form.lines.length === 0) return 'Ajoutez au moins une ligne.';
    if (!form.lines.some((l) => (l.description || '').trim() && Number(l.qty) > 0))
      return 'Ajoutez au moins une ligne avec description et quantité.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        supplierId: form.supplierId,
        supplierInvoiceNumber: form.supplierInvoiceNumber.trim(),
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate || undefined,
        currency: form.currency || 'TND',
        status: form.status || 'VALIDATED',
        lines: totals.lines.map((l) => ({
          description: (l.description || '').trim(),
          qty: Number(l.qty) || 0,
          unitPriceHT: Number(l.unitPriceHT) || 0,
          tvaRate: Number(l.tvaRate) || 19,
          discountType: l.discountType || 'PERCENT',
          discountValue: Number(l.discountValue) || 0,
          stockItemId: l.stockItemId || null,
        })),
        tvaRate: Number(form.tvaRate) || 0,
        discountType: form.discountType || 'PERCENT',
        discountValue: Number(form.discountValue) || 0,
        stampDuty: Number(form.stampDuty) || 0,
        notes: (form.notes || '').trim() || undefined,
      };
      const id = editingInvoice?._id || editingInvoice?.id;
      if (id) {
        await purchaseInvoicesService.update(id, payload);
      } else {
        const created = await purchaseInvoicesService.create(payload);
        // ── Attacher automatiquement le fichier OCR si disponible ──
        if (ocrFile && (created?._id || created?.id)) {
          try {
            await purchaseInvoicesService.uploadAttachment(created._id || created.id, ocrFile);
          } catch (attachErr) {
            console.warn('[OCR] Échec attachement:', attachErr.message);
          }
          setOcrFile(null);
        }
      }
      closeModal();
      await loadInvoices();
    } catch (e2) {
      const msg = e2?.response?.data?.error || e2?.response?.data?.message || e2?.message || "Erreur lors de l'enregistrement.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (inv) => {
    const id = inv?._id || inv?.id;
    if (!id) { setError('Impossible de supprimer: identifiant manquant.'); return; }
    if (!await showConfirm("Êtes-vous sûr de vouloir supprimer cette facture d'achat ?")) return;
    setLoading(true); setError('');
    try { await purchaseInvoicesService.remove(id); await loadInvoices(); }
    catch (e) { setError(e?.response?.data?.message || e?.message || 'Erreur lors de la suppression.'); }
    finally { setLoading(false); }
  };

  const handleUploadAttachment = async (inv, file) => {
    const id = inv?._id || inv?.id;
    if (!id) return setError('Impossible de joindre: identifiant manquant.');
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.type?.startsWith('image/'))
      return setError('Type non autorisé (PDF ou image uniquement).');
    setUploadingId(id); setError('');
    try {
      await purchaseInvoicesService.uploadAttachment(id, file);
      await loadInvoices();
      if (editingInvoice && (editingInvoice._id === id || editingInvoice.id === id)) {
        const updated = (await purchaseInvoicesService.getById?.(id)) || null;
        if (updated) setEditingInvoice(updated);
      }
    } catch (e) { setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Erreur upload.'); }
    finally { setUploadingId(null); }
  };

  const handleViewAttachment = async (inv) => {
    const id = inv?._id || inv?.id;
    if (!id) return setError("Impossible d'ouvrir: identifiant manquant.");
    try {
      setUploadingId(id);
      const response = await purchaseInvoicesService.getAttachmentBlob(id);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (e) { setError(e?.response?.data?.error || e?.message || 'Erreur ouverture fichier.'); }
    finally { setUploadingId(null); }
  };

  const handleDeleteAttachment = async (inv) => {
    const id = inv?._id || inv?.id;
    if (!id) return setError("Identifiant manquant.");
    if (!await showConfirm("Supprimer la pièce jointe ?")) return;
    try { setUploadingId(id); await purchaseInvoicesService.deleteAttachment(id); await loadInvoices(); }
    catch (e) { setError(e?.response?.data?.message || e?.message || "Erreur suppression PJ."); }
    finally { setUploadingId(null); }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/)) { setError("Fichier Excel invalide (.xlsx ou .xls)"); return; }
    setImportingExcel(true); setError('');
    try {
      const result = await purchaseInvoicesService.importExcel(file);
      if (result.success) { toastSuccess(`Succès : ${result.count} factures importées.`); await loadInvoices(); }
    } catch (err) { setError(err?.response?.data?.error || "Erreur lors de l'importation Excel."); }
    finally { setImportingExcel(false); e.target.value = null; }
  };

  // ── Helper : remplir le formulaire depuis les données OCR ──
  const _applyOcrDataToForm = (ocrData) => {
    let supplierId = '';
    if (ocrData.supplierMF && suppliers.length > 0) {
      const mfNorm = ocrData.supplierMF.replace(/[\s\/]/g, '').toUpperCase();
      const found = suppliers.find(s => (s.mf || '').replace(/[\s\/]/g, '').toUpperCase() === mfNorm);
      if (found) supplierId = found._id || found.id;
    }
    if (!supplierId && ocrData.supplierName && suppliers.length > 0) {
      const found = suppliers.find(s =>
        s.name?.toLowerCase().includes(ocrData.supplierName.toLowerCase()) ||
        ocrData.supplierName.toLowerCase().includes(s.name?.toLowerCase())
      );
      if (found) supplierId = found._id || found.id;
    }
    setForm({
      supplierId,
      supplierInvoiceNumber: ocrData.supplierInvoiceNumber || '',
      invoiceDate: ocrData.invoiceDate || new Date().toISOString().split('T')[0],
      dueDate: ocrData.dueDate || '',
      currency: ocrData.currency || 'TND',
      status: 'DRAFT',
      lines: ocrData.lines?.length > 0 ? ocrData.lines : [{ description: '', qty: 1, unitPriceHT: 0 }],
      tvaRate: ocrData.tvaRate || 19,
      discountType: 'PERCENT',
      discountValue: 0,
      stampDuty: ocrData.stampDuty ?? 1,
      notes: ocrData.supplierName
        ? `Fournisseur détecté: ${ocrData.supplierName}${ocrData.supplierMF ? ` (MF: ${ocrData.supplierMF})` : ''}`
        : '',
    });
  };

  // ── OCR : handler principal ──
  const handleOcrComplete = async (file) => {
    setOcrFile(file); // stocker le fichier pour attachement auto après création
    setOcrProcessing(true); setError('');
    try {
      const result = await purchaseInvoicesService.processOcrAuto(file);
      if (!result.success) throw new Error(result.error || 'Erreur OCR');

      // Cas 1 : création automatique réussie (fournisseur trouvé + pas de warnings)
      if (result.autoCreated) {
        setShowOcrModal(false);
        setOcrFile(null);
        await loadInvoices();
        toastInfo(`Facture créée automatiquement !\n\nFournisseur : ${result.supplier.name}\nNuméro : ${result.invoice.supplierInvoiceNumber}\nMontant : ${result.invoice.totalTTC?.toFixed(2) || 0} DT\n\nLa pièce jointe a été automatiquement attachée.`);
        return;
      }

      const ocrData = result.data;

      // Cas 2 : warnings workspace → afficher modale de confirmation
      const warnings = result.warnings || ocrData?.warnings || [];
      if (warnings.length > 0) {
        _applyOcrDataToForm(ocrData);
        setShowOcrModal(false);
        setOcrWarnings({ warnings, invoiceRef: ocrData.supplierInvoiceNumber });
        return;
      }

      // Cas 3 : pas de warnings, fournisseur non trouvé → formulaire manuel
      _applyOcrDataToForm(ocrData);
      setShowModal(true);
      setShowOcrModal(false);
      if (result.message) {
        setError(`⚠️ ${result.message}\n\nVeuillez sélectionner le fournisseur manuellement.`);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Erreur OCR.');
      throw e;
    } finally {
      setOcrProcessing(false);
    }
  };

  // ── Callbacks modale confirmation warnings ──
  const handleWarningConfirm = () => {
    setOcrWarnings(null);
    setShowModal(true); // ouvrir le formulaire malgré les warnings
  };

  const handleWarningCancel = () => {
    setOcrWarnings(null);
    setOcrFile(null);
    setForm(emptyInvoice);
  };

  return (
    <div className="flex-1 p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Factures achats</h1>
        <p className="text-gray-600">Créez et suivez vos factures fournisseurs.</p>
      </div>

      {/* Top toolbar */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Liste des factures</h3>
          <p className="text-sm text-gray-500">{filteredInvoices.length} facture(s)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} disabled={filteredInvoices.length === 0}>
            <IconFileExport size={15} strokeWidth={1.6} className="mr-1" /> Export
          </Button>
          <Button variant="secondary" size="sm" onClick={loadInvoices} disabled={loading}>
            <Icon name="IconSync" size={16} className="mr-1" /> Rafraîchir
          </Button>
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <a
            href="/modele_import_factures_achats_TEJ.xlsx"
            download="modele_import_factures_achats_TEJ.xlsx"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
            title="Telecharger le modele Excel a remplir"
          >
            <Icon name="IconDownload" size={14} className="mr-1" />
            Modele Excel
          </a>
          <div className="relative">
            <input type="file" id="import-excel-input" accept=".xlsx, .xls" className="hidden" onChange={handleImportExcel} disabled={importingExcel} />
            <label htmlFor="import-excel-input" className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer ${importingExcel ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <Icon name={importingExcel ? 'IconSpinner' : 'IconFileSpreadsheet'} size={14} className={`mr-1 ${importingExcel ? 'animate-spin' : ''}`} />
              {importingExcel ? 'Importation...' : 'Importer Excel'}
            </label>
          </div>
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <Button variant="secondary" size="sm" onClick={() => setShowOcrModal(true)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
            <Icon name="IconMagic" size={16} className="mr-1" /> Créer depuis facture
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <Icon name="IconPlus" size={16} className="mr-1" /> Nouvelle facture
          </Button>
        </div>
      </div>

      {!!error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800 font-semibold">Erreur</div>
          <div className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</div>
        </div>
      )}

      {/* Filtre */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="p-5 border-b border-gray-200"><h4 className="font-semibold text-gray-800">Recherche</h4></div>
        <div className="px-5 pb-5">
          <label className="block text-sm font-medium mb-2 text-gray-700">Fournisseur / Numéro / Statut</label>
          <Input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Ex: ABC, F-2026-15, PAID..." />
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
            <p className="mt-2 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Fournisseur</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">N° facture</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Statut</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Total TTC</th>
                  <th className="p-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedInvoices.map((inv) => {
                  const id = inv?._id || inv?.id;
                  const supplierObj = inv?.supplierId && typeof inv.supplierId === 'object' ? inv.supplierId : null;
                  const supplier = supplierObj || supplierById.get(supplierObj?._id || inv?.supplierId);
                  const hasAttachment = !!(inv?.attachment);
                  return (
                    <tr key={id || inv?.supplierInvoiceNumber} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <div className="font-medium text-gray-900 text-sm truncate max-w-[240px]">{supplier?.name || '—'}</div>
                        <div className="text-xs text-gray-500 font-mono truncate max-w-[240px]">{supplier?.mf || ''}</div>
                      </td>
                      <td className="p-3"><div className="font-mono text-xs text-gray-800 truncate max-w-[160px]">{inv?.supplierInvoiceNumber || '—'}</div></td>
                      <td className="p-3"><div className="text-sm text-gray-800">{inv?.invoiceDate || '—'}</div></td>
                      <td className="p-3"><Badge variant="gray">{inv?.status || '—'}</Badge></td>
                      <td className="p-3"><div className="font-semibold text-gray-900 text-sm">{((inv?.grandTotal ?? inv?.totalTTC ?? 0)).toFixed(2)} DT</div></td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                            <label htmlFor={`file-${id}`} className={`flex items-center justify-center w-8 h-8 cursor-pointer hover:bg-gray-100 transition-colors ${uploadingId === id ? 'opacity-60 pointer-events-none' : ''} ${hasAttachment ? 'text-blue-600 bg-blue-50' : 'text-gray-500'}`} title={hasAttachment ? 'Remplacer' : 'Joindre'}>
                              <Icon name={uploadingId === id ? 'IconSpinner' : 'IconPaperclip'} size={14} className={uploadingId === id ? 'animate-spin' : ''} />
                            </label>
                            <input id={`file-${id}`} type="file" accept="application/pdf,image/*" className="hidden" onClick={(e) => { e.currentTarget.value = null; }} onChange={(e) => handleUploadAttachment(inv, e.target.files?.[0])} />
                            {hasAttachment && (<>
                              <div className="w-px h-4 bg-gray-200" />
                              <button className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-gray-100 transition-colors" onClick={() => handleViewAttachment(inv)} title="Voir" type="button"><Icon name="IconEye" size={14} /></button>
                              <div className="w-px h-4 bg-gray-200" />
                              <button className="flex items-center justify-center w-8 h-8 text-red-500 hover:bg-red-50 transition-colors" onClick={() => handleDeleteAttachment(inv)} title="Supprimer PJ" type="button"><Icon name="IconUnlink" size={14} /></button>
                            </>)}
                          </div>
                          <div className="w-px h-5 bg-gray-200" />
                          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                            <button className="flex items-center justify-center w-8 h-8 text-amber-600 hover:bg-amber-50 transition-colors" onClick={() => openEdit(inv)} title="Modifier" type="button"><Icon name="IconEdit" size={14} /></button>
                            <div className="w-px h-4 bg-gray-200" />
                            <button className="flex items-center justify-center w-8 h-8 text-red-500 hover:bg-red-50 transition-colors" onClick={() => handleDelete(inv)} title="Supprimer" type="button"><Icon name="IconTrash" size={14} /></button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!loading && filteredInvoices.length === 0 && (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4"><Icon name="IconFileInvoice" size={24} className="text-gray-400" /></div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">Aucune facture</h3>
                <p className="text-gray-500 mb-6">Créez votre première facture d'achat.</p>
                <Button variant="primary" size="md" onClick={openCreate}><Icon name="IconPlus" size={16} /> Nouvelle facture</Button>
              </div>
            )}
          </div>
        )}
        {!loading && filteredInvoices.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {filteredInvoices.length} facture(s) — Page {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronLeft size={16} strokeWidth={1.6} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-800">{editingInvoice ? 'Modifier la facture achat' : 'Nouvelle facture achat'}</h2>
                <p className="text-xs text-gray-500 mt-0.5">TVA au niveau facture, remise avant TVA, timbre inclus.</p>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {editingInvoice ? (() => {
                    const id = editingInvoice?._id || editingInvoice?.id;
                    const hasAttachment = !!editingInvoice?.attachment?.storedName;
                    const inputId = `purchase-invoice-file-modal-${id}`;
                    return (<>
                      <label htmlFor={inputId} className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-colors ${uploadingId === id ? 'opacity-60 pointer-events-none bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                          {uploadingId === id ? <Icon name="IconSpinner" size={14} className="animate-spin" /> : <Icon name="IconPaperclip" size={14} />}
                          {hasAttachment ? 'Remplacer' : 'Joindre'}
                        </label>
                      <input id={inputId} type="file" accept="application/pdf,image/*" className="hidden" onClick={(e) => { e.currentTarget.value = null; }} onChange={(e) => handleUploadAttachment(editingInvoice, e.target.files?.[0])} />
                      {hasAttachment && (<>
                        <Button variant="secondary" size="sm" onClick={() => handleViewAttachment(editingInvoice)}><Icon name="IconEye" size={14} /> Voir</Button>
                        <Button variant="danger" size="sm" onClick={() => handleDeleteAttachment(editingInvoice)}><Icon name="IconUnlink" size={14} /> Supprimer</Button>
                      </>)}
                    </>);
                  })() : <span className="text-xs text-gray-500">Enregistrez la facture d'abord pour joindre un fichier.</span>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={closeModal} aria-label="Fermer" type="button">
                <Icon name="IconX" size={20} />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {!!error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-800 font-semibold">Erreur</div>
                  <div className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label required>Fournisseur</Label>
                    <Select value={form.supplierId} onChange={(e) => setField('supplierId', e.target.value)}>
                      <option value="">-- Sélectionner --</option>
                      {suppliers.map((s) => { const id = s._id || s.id; return (<option key={id} value={id}>{s.name} {s.mf ? `(${s.mf})` : ''}</option>); })}
                    </Select>
                    {suppliers.length === 0 && <p className="text-xs text-orange-600 mt-1">Aucun fournisseur chargé.</p>}
                  </div>
                  <div>
                    <Label required>N° facture fournisseur</Label>
                    <Input value={form.supplierInvoiceNumber} onChange={(e) => setField('supplierInvoiceNumber', e.target.value)} className="font-mono" placeholder="Ex: F-2026-145" />
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Select value={form.status} onChange={(e) => setField('status', e.target.value)}>
                      <option value="VALIDATED">VALIDATED</option>
                      <option value="PARTIALLY_PAID">PARTIALLY_PAID</option>
                      <option value="PAID">PAID</option>
                      <option value="CANCELED">CANCELED</option>
                    </Select>
                  </div>
                  <div>
                    <Label required>Date facture</Label>
                    <Input type="date" value={form.invoiceDate} onChange={(e) => setField('invoiceDate', e.target.value)} />
                  </div>
                  <div>
                    <Label>Échéance</Label>
                    <Input type="date" value={form.dueDate} onChange={(e) => setField('dueDate', e.target.value)} />
                  </div>
                  <div>
                    <Label>Devise</Label>
                    <Input value={form.currency} onChange={(e) => setField('currency', e.target.value)} placeholder="TND" />
                  </div>
                </div>
                <div className="bg-gray-50 border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">Lignes</h3>
                    <Button variant="ghost" size="sm" type="button" onClick={addLine}><Icon name="IconPlus" size={14} /> Ajouter ligne</Button>
                  </div>
                  <div className="mt-4 space-y-4">
                    {totals.lines.map((l, idx) => (
                      <div key={idx} className="bg-white border rounded-lg p-4">
                        {workspaceStockEnabled && (
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mb-3 pb-3 border-b border-dashed border-gray-200">
                            <div className="md:col-span-10">
                              <label className="block text-xs font-medium mb-1 text-blue-700">Article (Stock)</label>
                              <div className="flex gap-2">
                                <select
                                  value={l.stockItemId || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) {
                                      updateLine(idx, { stockItemId: null, description: '', unitPriceHT: 0 });
                                      return;
                                    }
                                    const item = stockItems.find(s => (s._id || s.id) === val);
                                    if (item) {
                                      updateLine(idx, {
                                        stockItemId: item._id || item.id,
                                        description: item.name,
                                        unitPriceHT: item.purchasePrice || 0,
                                      });
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                >
                                  <option value="">-- Sélectionner un article --</option>
                                  {stockItems.map((s) => {
                                    const sid = s._id || s.id;
                                    return (
                                      <option key={sid} value={sid}>
                                        {s.name} {s.sku ? `(${s.sku})` : ''} — {s.sellingPrice || 0} DT
                                      </option>
                                    );
                                  })}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => window.open('/stock/items', '_blank')}
                                  className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors shrink-0"
                                  title="Ajouter un article"
                                >
                                  + Nouveau
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mb-3">
                          <div className="md:col-span-6">
                            <label className="block text-xs font-medium mb-1 text-gray-700">Description</label>
                            <Input value={l.description || ''} onChange={(e) => updateLine(idx, { description: e.target.value })} placeholder="Ex: Achat consommables..." />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium mb-1 text-gray-700">Qté</label>
                            <Input type="number" step="1" value={l.qty} onChange={(e) => updateLine(idx, { qty: e.target.value })} />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium mb-1 text-gray-700">PU HT</label>
                            <Input type="number" step="0.001" value={l.unitPriceHT} onChange={(e) => updateLine(idx, { unitPriceHT: e.target.value })} />
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-xs font-medium mb-1 text-gray-700">Total HT</label>
                            <div className="p-2.5 border border-gray-200 bg-white rounded-lg text-sm font-semibold text-gray-800">{l.lineTotalHT.toFixed(2)} DT</div>
                          </div>
                          <div className="md:col-span-1 flex justify-end">
                            <Button variant="danger" size="sm" type="button" onClick={() => removeLine(idx)} title="Supprimer ligne"><Icon name="IconTrash" size={14} /></Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end pt-3 border-t border-gray-200">
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Remise</label>
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={l.discountType || 'PERCENT'} onChange={(e) => updateLine(idx, { discountType: e.target.value })}><option value="PERCENT">%</option><option value="AMOUNT">DT</option></Select>
                              <Input type="number" step="0.01" value={l.discountValue || 0} onChange={(e) => updateLine(idx, { discountValue: e.target.value })} />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Taux TVA %</label>
                            <Select value={l.tvaRate || 19} onChange={(e) => updateLine(idx, { tvaRate: e.target.value })}>
                              <option value={0}>0%</option><option value={7}>7%</option><option value={13}>13%</option><option value={19}>19%</option>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Net HT</label>
                            <div className="p-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm font-semibold text-gray-700">{(l.lineNetHT || 0).toFixed(2)} DT</div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Total TTC</label>
                            <div className="p-2.5 border border-gray-200 bg-blue-50 rounded-lg text-sm font-bold text-blue-800">{(l.lineTotalTTC || 0).toFixed(2)} DT</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Timbre fiscal (DT)</Label>
                    <Input type="number" step="0.01" value={form.stampDuty} onChange={(e) => setField('stampDuty', e.target.value)} />
                  </div>
                </div>
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Résumé par Taux TVA</h3>
                  <table className="w-full text-sm mb-3">
                    <thead><tr className="border-b border-gray-300"><th className="text-left py-2 px-2 font-medium text-gray-700">Taux</th><th className="text-right py-2 px-2 font-medium text-gray-700">Sous-total HT</th><th className="text-right py-2 px-2 font-medium text-gray-700">TVA</th><th className="text-right py-2 px-2 font-medium text-gray-700">Total TTC</th></tr></thead>
                    <tbody>
                      {[0, 7, 13, 19].map((rate) => {
                        const d = totals.tvaByRate?.[rate] || { subtotal: 0, tva: 0, total: 0 };
                        return (<tr key={rate} className="border-b border-gray-200 hover:bg-gray-50"><td className="py-2 px-2 font-medium text-gray-800">{rate}%</td><td className="text-right py-2 px-2 text-gray-700">{d.subtotal.toFixed(3)} DT</td><td className="text-right py-2 px-2 text-gray-700">{d.tva.toFixed(3)} DT</td><td className="text-right py-2 px-2 font-semibold text-gray-900">{d.total.toFixed(3)} DT</td></tr>);
                      })}
                    </tbody>
                  </table>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 mb-3">
                    <div className="flex justify-between text-sm"><span className="text-gray-700">Total Remise :</span><span className="font-semibold text-orange-600">{(totals.totalLineDiscounts || 0).toFixed(3)} DT</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-700">Total TVA :</span><span className="font-semibold text-blue-600">{(totals.totalTVA || 0).toFixed(3)} DT</span></div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center">
                    <span className="font-semibold text-gray-800">GRAND TOTAL (TTC + Timbre) :</span>
                    <span className="text-lg font-bold text-blue-800">{(totals.grandTotal || 0).toFixed(3)} DT</span>
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder="Remarques internes..." />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="secondary" type="button" onClick={closeModal} disabled={loading}>Annuler</Button>
                  <Button variant="primary" type="submit" disabled={loading}>{loading ? 'Enregistrement...' : 'Enregistrer'}</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <OcrUploadModal show={showOcrModal} onClose={() => setShowOcrModal(false)} onOcrComplete={handleOcrComplete} />

      {/* ── Modale de confirmation warnings workspace ── */}
      {ocrWarnings && (
        <InvoiceImportConfirm
          warnings={ocrWarnings.warnings}
          invoiceRef={ocrWarnings.invoiceRef}
          onConfirm={handleWarningConfirm}
          onCancel={handleWarningCancel}
        />
      )}

      {showExportModal && (
        <ExportModal
          columns={purchaseInvoiceColumns}
          data={filteredInvoices}
          title="Liste des factures achats"
          filename="factures_achats"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
